import { FC, useRef } from "react";

import { cn } from "@src/lib/utils/cn";
import { RadioGroupItem } from "@src/lib/ui/RadioGroup";

type Props = {
  className?: string;
  testId?: string;
  value: string;
  children: React.ReactNode;
  disabled?: boolean;
  selected?: boolean;
  // Hide the visual radio dot for card-only designs. Screen readers
  // still see it (sr-only preserves the radio role + keyboard nav).
  hideIndicator?: boolean;
  // Names the radio itself; the visible children aren't linked to it.
  ariaLabel?: string;
  // "row": [icon] label … radio, full width. "card": icon and radio on top,
  // label at the bottom; lay cards out in a grid on the RadioGroup.
  variant?: "row" | "card";
  // Rendered in a small tile: left of the label (row), top-left (card).
  icon?: React.ReactNode;
};

// Selected styling follows the radio's own checked state, so it also applies
// when the caller doesn't pass `selected`.
const SELECTED_CLASSNAME = "border-brand-border bg-brand-bg hover:bg-brand-bg";
const CHECKED_CLASSNAME =
  "has-[[role=radio][data-state=checked]]:border-brand-border has-[[role=radio][data-state=checked]]:bg-brand-bg has-[[role=radio][data-state=checked]]:hover:bg-brand-bg";

// Disabled keeps the selection readable with a stronger neutral outline.
const DISABLED_SELECTED_CLASSNAME = "border-border-tertiary";
const DISABLED_CHECKED_CLASSNAME =
  "has-[[role=radio][data-state=checked]]:border-border-tertiary";

const RadioGroupChoiceboxItem: FC<Props> = ({
  className,
  testId,
  value,
  children,
  disabled,
  selected,
  hideIndicator,
  ariaLabel,
  variant = "row",
  icon,
}) => {
  const itemRef = useRef<HTMLButtonElement>(null);
  const isCard = variant === "card";

  const iconTile = icon ? (
    <span
      className={cn(
        "flex size-8 shrink-0 items-center justify-center rounded-lg bg-bg-tertiary text-icon-secondary transition-colors",
        disabled
          ? "text-icon-disabled"
          : cn(
              "group-has-[[role=radio][data-state=checked]]/choicebox:bg-bg-primary group-has-[[role=radio][data-state=checked]]/choicebox:text-brand-text",
              selected && "bg-bg-primary text-brand-text",
            ),
        !isCard && "mr-3",
      )}
    >
      {icon}
    </span>
  ) : null;

  const radio = (
    <RadioGroupItem
      aria-label={ariaLabel}
      disabled={disabled}
      data-testid={testId ? testId : undefined}
      className={cn(
        // The whole box carries the focus ring.
        "focus-visible:ring-0",
        !isCard && "ml-auto",
        hideIndicator && "sr-only size-px border-0",
      )}
      value={value}
      ref={itemRef}
    />
  );

  return (
    <div
      data-variant={variant}
      className={cn(
        "group/choicebox flex rounded-xl border border-border-primary bg-bg-primary p-3 text-text-primary transition-colors",
        isCard ? "flex-col items-stretch gap-4" : "items-center",
        "has-[[role=radio]:focus-visible]:ring-[3px] has-[[role=radio]:focus-visible]:ring-brand-border",
        disabled
          ? cn(
              "cursor-not-allowed bg-bg-subtle text-text-disabled [&>*]:cursor-not-allowed",
              DISABLED_CHECKED_CLASSNAME,
            )
          : cn(
              "cursor-pointer hover:bg-state-hover [&>*]:cursor-pointer",
              CHECKED_CLASSNAME,
            ),
        className,
        selected &&
          (disabled ? DISABLED_SELECTED_CLASSNAME : SELECTED_CLASSNAME),
      )}
      onClick={() => {
        if (itemRef.current) {
          itemRef.current.click();
        }
      }}
    >
      {isCard ? (
        <>
          <div className="flex items-start justify-between gap-3">
            {iconTile ?? <span />}
            {radio}
          </div>
          {children}
        </>
      ) : (
        <>
          {iconTile}
          {children}
          {radio}
        </>
      )}
    </div>
  );
};

export default RadioGroupChoiceboxItem;
